// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useMemo } from 'react';

import { Text } from '@/components/Themed';
import { type AvailableMember } from '@/components/PlayerMembershipEditor';
import {
  buildMembershipCandidatePool,
  buildMembershipOptionsByQuery,
} from '@/components/playerMembershipDraft';

// Search results are already capped server-side, but keep a ceiling so a broad query cannot flood
// the list.
const MAX_RESULTS = 50;

export type AddedPlayer = { id: string; name: string };

type Props = {
  visible: boolean;
  candidates: AvailableMember[];
  addedPlayers: AddedPlayer[];
  // Controlled by the parent so it can run a server-side search: the candidate list only holds
  // people the caller already knows, and anyone else has to be fetched for the typed query.
  query: string;
  onQueryChange: (query: string) => void;
  searching?: boolean;
  title: string;
  addedLabel: string;
  newPlayerLabel: string;
  pickFriendLabel: string;
  searchPlaceholder: string;
  noResults: string;
  closeLabel: string;
  doneLabel: string;
  onSelectFriend: (member: AvailableMember) => void;
  onRemoveFriend: (member: AddedPlayer) => void;
  onCreateNew: () => void;
  onClose: () => void;
};

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function AddPlayerModal({
  visible,
  candidates,
  addedPlayers,
  query,
  onQueryChange,
  searching = false,
  title,
  addedLabel,
  newPlayerLabel,
  pickFriendLabel,
  searchPlaceholder,
  noResults,
  closeLabel,
  doneLabel,
  onSelectFriend,
  onRemoveFriend,
  onCreateNew,
  onClose,
}: Props) {
  const addedIds = useMemo(() => new Set(addedPlayers.map((player) => player.id)), [addedPlayers]);

  const filtered = useMemo(
    () =>
      buildMembershipOptionsByQuery(
        buildMembershipCandidatePool(candidates, query),
        query,
        MAX_RESULTS
      ),
    [candidates, query]
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{title}</Text>
            <Pressable style={styles.closeButton} onPress={onClose} accessibilityLabel={closeLabel}>
              <Text style={styles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          {addedPlayers.length > 0 ? (
            <View style={styles.addedSection}>
              <Text style={styles.addedLabel}>{`${addedLabel} (${addedPlayers.length})`}</Text>
              <View style={styles.chips}>
                {addedPlayers.map((player) => (
                  <Pressable
                    key={player.id}
                    style={styles.chip}
                    onPress={() => onRemoveFriend(player)}
                    accessibilityLabel={player.name}
                  >
                    <Text style={styles.chipText} numberOfLines={1}>
                      {player.name}
                    </Text>
                    <Text style={styles.chipRemove}>✕</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          ) : null}

          <Pressable style={styles.newPlayerButton} onPress={onCreateNew} accessibilityLabel={newPlayerLabel}>
            <Text style={styles.newPlayerButtonText}>{`+  ${newPlayerLabel}`}</Text>
          </Pressable>

          <View style={styles.dividerRow}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>{pickFriendLabel}</Text>
            <View style={styles.dividerLine} />
          </View>

          <TextInput
            value={query}
            onChangeText={onQueryChange}
            placeholder={searchPlaceholder}
            placeholderTextColor="#8FA08B"
            style={styles.search}
            autoCorrect={false}
            autoCapitalize="none"
          />

          <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
            {filtered.length === 0 ? (
              <Text style={styles.empty}>{searching ? '…' : noResults}</Text>
            ) : (
              filtered.map((member) => {
                const added = addedIds.has(member.id);
                return (
                  <Pressable
                    key={member.id}
                    style={[styles.friendRow, added && styles.friendRowAdded]}
                    onPress={() => (added ? onRemoveFriend(member) : onSelectFriend(member))}
                    accessibilityLabel={member.name}
                    accessibilityState={{ selected: added }}
                  >
                    <View style={styles.avatar}>
                      <Text style={styles.avatarText}>{initialsOf(member.name)}</Text>
                    </View>
                    <Text style={styles.friendName} numberOfLines={1}>
                      {member.name}
                    </Text>
                    <View style={[styles.addIcon, added && styles.addedIcon]}>
                      <Text style={[styles.addIconText, added && styles.addedIconText]}>{added ? '✓' : '+'}</Text>
                    </View>
                  </Pressable>
                );
              })
            )}
          </ScrollView>

          <Pressable style={styles.doneButton} onPress={onClose} accessibilityLabel={doneLabel}>
            <Text style={styles.doneButtonText}>{doneLabel}</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    maxHeight: '82%',
    backgroundColor: '#07200D',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#1A3A20',
    padding: 16,
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: {
    flex: 1,
    color: '#F5F5DC',
    fontSize: 18,
    fontWeight: '700',
  },
  closeButton: {
    width: 30,
    height: 30,
    borderRadius: 15,
    borderWidth: 1,
    borderColor: '#1A3A20',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    color: '#8FA08B',
    fontSize: 16,
    fontWeight: '700',
  },
  addedSection: {
    gap: 8,
  },
  addedLabel: {
    color: '#8FA08B',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingLeft: 12,
    paddingRight: 10,
    borderRadius: 999,
    backgroundColor: '#123018',
    borderWidth: 1,
    borderColor: '#1A3A20',
  },
  chipText: {
    color: '#F5F5DC',
    fontSize: 13,
    fontWeight: '700',
    maxWidth: 160,
  },
  chipRemove: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '700',
  },
  newPlayerButton: {
    backgroundColor: '#A1D493',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  newPlayerButtonText: {
    color: '#011806',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A3A20',
  },
  dividerText: {
    color: '#8FA08B',
    fontSize: 10,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  search: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#0D2A13',
    color: '#F5F5DC',
    fontSize: 14,
  },
  list: {
    maxHeight: 260,
  },
  empty: {
    color: '#8FA08B',
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 16,
  },
  friendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 10,
  },
  friendRowAdded: {
    backgroundColor: '#0D2A13',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#123018',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#A1D493',
    fontSize: 12,
    fontWeight: '700',
  },
  friendName: {
    flex: 1,
    color: '#F5F5DC',
    fontSize: 14,
  },
  addIcon: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 1,
    borderColor: '#A1D493',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addIconText: {
    color: '#A1D493',
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 17,
  },
  addedIcon: {
    backgroundColor: '#A1D493',
    borderColor: '#A1D493',
  },
  addedIconText: {
    color: '#011806',
  },
  doneButton: {
    borderWidth: 1,
    borderColor: '#A1D493',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneButtonText: {
    color: '#A1D493',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
});
